<script lang="ts">
	// Today's orders of this till, and the line a cashier reconciles its
	// drawer against. Not a task, so it replaces the sell screen rather than
	// sitting over it, with Back at the inline start.
	//
	// This till only, from its own order store (#371, E16), and the trading
	// day rather than the calendar day: a shop that closes at 03:00 counts one
	// night under one date. Each order reads its till's prefix-N, A-12.
	//
	// Void sits in an order's details, so it is never one stray tap away in
	// the row. It then takes one tap on a reason, in place under the details.
	// Only a manager voids (#379): with anyone else signed in, the reason
	// opens the Manager approval sheet, which the till puts over this screen.
	// A no-sale drawer open takes one confirm tap, in place in the header.
	// Test print and Open drawer show inline notice lines when no receipt
	// printer or drawer is set up. The Printers button opens the shop's
	// printers (T25).
	//
	// An order's details name the discount it was given and what it took
	// (UC-35), from the copy the order kept.
	//
	// A paid or void order that has not reached Bosla says so under its row:
	// waiting to send. One the server refused reads the same, because the
	// store sends it on the stale track, which takes every sale (#382, N17).
	// None offers Retry, because only the store sends.
	//
	// The till's open orders of every trading day are listed too (#348), one
	// of an earlier day with its date. Open takes one back to Sell, to add to
	// it, change its discount or take its payment; it waits, and says Send
	// first, while Sell holds lines not sent yet, so none is lost.
	//
	// Waiting for a decision (#515) heads the screen while the till's store
	// holds a print or a kick because its delivery is uncertain: one row per
	// held job, whatever its age. Decide shows its choices in place, as Void
	// shows its reasons, and one tap on a choice sends that decision; No
	// leaves it. A refusal is said under its row, or above the list once the
	// row has gone because someone decided it.
	//
	// Each delivery names its driver as the order kept him, or "No driver" for
	// one an older screen sent (UC-4.R7). Once the day holds a delivery with a
	// driver, a row of drivers under the bar filters the list by one, beside
	// the status filter. An open delivery's details offer Change driver
	// (UC-66), which the till opens as Pick a driver in change mode, over this
	// screen, through Manager approval for anyone but a manager.
	//
	// For a manager, a standing Notice heads the screen while the till's store
	// has a newer Bosla app to show (UC-57, #650): Update app downloads it, a
	// meter shows how much came, and once it is kept the Notice says it
	// installs when the till locks with nothing on its way, and offers Install
	// now, which installs as soon as nothing is (#925). Orders reads it when
	// it opens and again on its own poll (src/till/appUpdate.svelte.ts). A
	// cashier who is not a manager sees nothing of it.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Meter from '../components/Meter.svelte';
	import Notice from '../components/Notice.svelte';
	import { AppUpdate, updateNotice } from '../till/appUpdate.svelte';
	import { voidReasons, type ReasonKey } from '../till/sell.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { fill, message, type Language, type MessageKey } from '../i18n/messages';
	import { leftToRight } from '../format/bidi';
	import { discountLabel } from '../format/discount';
	import { formatMinor } from '../format/money';
	import { orderNumber, payLine, tradingDayLabel } from '../format/order';
	import { formatClock } from '../format/time';
	import type { Sends } from '../till/sell.svelte';
	import { lineName } from '../till/weighed';
	import type { Decision, PrintJob } from '../till/printing';
	import type { components } from '../api/types';
	import type { components as storeComponents } from '../api/store';

	type Day = storeComponents['schemas']['DayBody'];
	type Order = storeComponents['schemas']['OrderBody'];
	type OrderLine = components['schemas']['QuotedLineBody'];
	type Filter = 'all' | 'open' | 'paid' | 'void';

	interface Props {
		language: Language;
		minorDigits: number;
		day: Day | null;
		/** The day is on its way and there is none to show yet. */
		loading: boolean;
		/** The day could not be fetched. */
		loadFailed: boolean;
		/**
		 * What is in flight, if anything: `print:<id>`, `void:<id>:<reason>`,
		 * `pay:<id>`, `nosale`, `test` or `decide:<jobId>:<decision>`. Every row
		 * action waits while one is.
		 */
		pending: string | null;
		/** The pending action has run past 300 ms, so it says what it is doing. */
		slow: boolean;
		/** A refusal that belongs to no row. */
		failure: string;
		/** The order whose void was refused because its day has closed. */
		voidClosedFor: string;
		/** Which closed orders have not reached Bosla, as the store last said. */
		sends: Sends | null;
		/** The filter the list opens on: the top bar's open count opens it on Open (#352). */
		startFilter?: Filter;
		hasReceiptPrinter: boolean;
		/** A manager is signed in, so Orders offers a newer Bosla app (UC-57). */
		isManager: boolean;
		/** Install now was pressed, and the till installs once nothing is on its way (#925). */
		installWaiting: boolean;
		/** Install now, on the Notice of a downloaded update (#925). */
		onInstallNow: () => void;
		/**
		 * The jobs the till's store holds for a decision (#515), each with its
		 * order's number as the till reads it ('' when it does not know it) and
		 * the choices it offers now.
		 */
		held: { job: PrintJob; number: string; choices: Decision[] }[];
		/** One tap on a held job's choice: that decision, sent at once. */
		onDecide: (job: PrintJob, decision: Decision) => void;
		/** Why the last decision did not go through, and the job it was on. */
		decideFailure: { jobId: string; text: string } | null;
		onReprint: (order: Order) => void;
		/** Sell holds no line waiting to be sent, so an open order may be opened on it. */
		canOpen: boolean;
		onOpen: (order: Order) => void;
		/** A void with its reason, by the preset's key and in the words shown. */
		onVoid: (order: Order, reasonKey: ReasonKey, reason: string) => void;
		/** Change driver on an open delivery (UC-66). */
		onChangeDriver: (order: Order) => void;
		onOpenDrawer: () => void;
		onTestPrint: () => void;
		/** Opens Shift (T29), this till's cash shift (UC-60). */
		onShift: () => void;
		onPrinters: () => void;
		onRetry: () => void;
		onBack: () => void;
	}

	let {
		language,
		minorDigits,
		day,
		loading,
		loadFailed,
		pending,
		slow,
		failure,
		voidClosedFor,
		sends,
		startFilter = 'all',
		hasReceiptPrinter,
		isManager,
		installWaiting,
		onInstallNow,
		held,
		onDecide,
		decideFailure,
		onReprint,
		canOpen,
		onOpen,
		onVoid,
		onChangeDriver,
		onOpenDrawer,
		onTestPrint,
		onPrinters,
		onShift,
		onRetry,
		onBack,
	}: Props = $props();

	// Orders mounts each time it is opened, so the filter it starts on is read once.
	// svelte-ignore state_referenced_locally
	let filter = $state<Filter>(startFilter);
	let voiding = $state('');
	let detail = $state('');
	let askingDrawer = $state(false);
	let localFailure = $state('');
	// The held job whose choices are open, as `voiding` is for Void's reasons.
	let deciding = $state('');

	// A refusal whose row has gone, because someone decided its job, is said above the list.
	const orphanFailure = $derived(
		decideFailure && !held.some((row) => row.job.id === decideFailure.jobId)
			? decideFailure.text
			: '',
	);
	const effectiveFailure = $derived(failure || localFailure || orphanFailure);

	// A printer set up meanwhile makes the notice stale.
	$effect(() => {
		if (hasReceiptPrinter) localFailure = '';
	});

	// A newer Bosla app, for a manager: read now and on Orders' own poll while
	// a manager is signed in, and shown as the store last said it.
	const appUpdate = new AppUpdate();
	$effect(() => {
		if (isManager) return appUpdate.watch();
	});
	const update = $derived(updateNotice(appUpdate.update, isManager));

	const busy = $derived(pending !== null);
	const all = $derived(day?.orders ?? []);
	// The driver the list is filtered by, by id, or '' for every order.
	let byDriver = $state('');
	// The drivers the day's deliveries name, each once, in the order they
	// first appear, under the name the newest of his orders kept.
	const dayDrivers = $derived.by(() => {
		// A scratch lookup, built and thrown away here; nothing reads it reactively.
		const seen: Record<string, true> = {};
		const out: { id: string; name: string }[] = [];
		for (const order of all) {
			if (order.type !== 'delivery' || !order.driverId || seen[order.driverId]) continue;
			seen[order.driverId] = true;
			out.push({ id: order.driverId, name: order.driverName ?? '' });
		}
		return out;
	});
	const driverFilters = $derived([
		{ value: '', label: message(language, 'driversFilterAll'), testId: 'orders-driver-all' },
		...dayDrivers.map((d) => ({ value: d.id, label: d.name, testId: `orders-driver-${d.id}` })),
	]);
	// A driver the day no longer names, after a new day's read, filters
	// nothing: the list shows every order, and the filter row All drivers.
	const driverShown = $derived(dayDrivers.some((d) => d.id === byDriver) ? byDriver : '');
	const orders = $derived(
		all.filter(
			(order) =>
				(filter === 'all' || order.status === filter) &&
				(driverShown === '' || order.driverId === driverShown),
		),
	);

	const filters = $derived(
		(
			[
				{ id: 'all', key: 'ordersToday' },
				{ id: 'open', key: 'ordersOpen' },
				{ id: 'paid', key: 'ordersPaid' },
				{ id: 'void', key: 'ordersVoid' },
			] as const
		).map((option) => ({
			value: option.id,
			label: message(language, option.key),
			testId: `orders-filter-${option.id}`,
		})),
	);

	const columns = $derived([
		{ label: message(language, 'ordersNumber'), w: '64px' },
		{ label: message(language, 'ordersTime'), w: '96px' },
		{ label: message(language, 'ordersType'), w: 'minmax(0,.8fr)' },
		{ label: message(language, 'ordersStatus'), w: '96px' },
		{ label: message(language, 'sellTotal'), w: '112px', align: 'end' as const },
		{ label: '', w: 'minmax(0,1.6fr)' },
	]);

	const heldColumns = $derived([
		{ label: message(language, 'ordersType'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'ordersNumber'), w: '96px' },
		{ label: message(language, 'ordersTime'), w: '96px' },
		{ label: '', w: 'minmax(0,1.6fr)' },
	]);

	const documents: Record<PrintJob['document'], MessageKey> = {
		receipt: 'printDocReceipt',
		bill: 'printDocBill',
		kitchen_slip: 'printDocKitchen',
		drawer: 'printDocDrawer',
		test: 'ordersTestPrint',
		shift_report: 'printDocShiftReport',
	};

	// Each choice's button names its operation, and says what it is doing while it is on its way.
	function choiceOf(job: PrintJob, decision: Decision): { label: MessageKey; pending: MessageKey } {
		if (decision === 'confirm_received') {
			return {
				label: job.document === 'drawer' ? 'printDrawerOpened' : 'printHaveIt',
				pending: 'saving',
			};
		}
		if (decision === 'open_drawer_now') {
			return { label: 'printOpenDrawerNow', pending: 'ordersDrawerOpening' };
		}
		return { label: 'printAnotherCopy', pending: 'donePrinting' };
	}

	const summary = $derived(
		day
			? [
					{ key: 'ordersPaid', value: String(day.summary.paidCount) },
					{ key: 'ordersVoid', value: String(day.summary.voidCount) },
					{ key: 'ordersCash', value: formatMinor(day.summary.cashMinor, minorDigits) },
					{ key: 'ordersCard', value: formatMinor(day.summary.cardMinor, minorDigits) },
					{ key: 'ordersDue', value: formatMinor(day.summary.dueMinor, minorDigits) },
					{ key: 'ordersNoSale', value: String(day.summary.noSaleCount) },
				]
			: [],
	);

	const types: Record<string, MessageKey> = {
		dine_in: 'sellDineIn',
		takeaway: 'sellTakeaway',
		delivery: 'sellDeliveryType',
	};

	// First-strong isolate and its end, by code point: a table's name keeps
	// its own direction inside the other language's line (UC-39).
	const ISOLATE = String.fromCodePoint(0x2068);
	const POP_ISOLATE = String.fromCodePoint(0x2069);

	// The order's type, and for a dine-in at a table the table as the order
	// kept it: "Dine in · Table T5"; a delivery's driver as it kept him:
	// "Delivery · Driver: Ahmed", or "Delivery · No driver".
	function typeOf(order: Order): string {
		const type = message(language, types[order.type] ?? 'sellDineIn');
		if (order.type === 'delivery') {
			const driver = order.driverName
				? fill(message(language, 'driverLabel'), ISOLATE + order.driverName + POP_ISOLATE)
				: message(language, 'driverNone');
			return `${type} · ${driver}`;
		}
		if (!order.tableName) return type;
		const table = fill(message(language, 'tableLabel'), ISOLATE + order.tableName + POP_ISOLATE);
		return `${type} · ${table}`;
	}

	// Paid is money counted, the ledger green. Open is money still to come,
	// something to know. A void is neither.
	function statusOf(order: Order): { key: MessageKey; tone: 'ok' | 'flag' | 'quiet' } {
		if (order.status === 'paid') return { key: 'ordersPaid', tone: 'ok' };
		if (order.status === 'void') return { key: 'ordersVoid', tone: 'quiet' };
		return { key: 'ordersOpen', tone: 'flag' };
	}

	function printed(count: number): string {
		return count === 1
			? `${message(language, 'ordersPrinted')} ${message(language, 'ordersPrintedOnce')}`
			: `${message(language, 'ordersPrinted')} ${count} ${message(language, 'ordersPrintedTimes')}`;
	}

	function modifiersOf(line: OrderLine): string {
		return (line.modifiers ?? []).map((m) => m.name).join(' · ');
	}

	function pendingState(key: string): 'pending' | 'idle' {
		return pending === key && slow ? 'pending' : 'idle';
	}
</script>

<div class="orders" data-testid="panel-orders" data-scroll="">
	<div class="bar">
		<Button
			testId="orders-back"
			icon="back"
			mirrorIcon={language === 'en'}
			label={message(language, 'ordersBack')}
			tone="secondary"
			size="sm"
			onPress={onBack}
		/>
		<h2>{message(language, 'ordersTillHeading')}</h2>
		<Segmented
			label={message(language, 'ordersStatus')}
			options={filters}
			value={filter}
			onChange={(value) => (filter = value as Filter)}
		/>
		<span class="grow"></span>
		{#if askingDrawer}
			<span class="ask" data-testid="orders-drawer-confirm">
				{message(language, 'ordersDrawerAsk')}
			</span>
			<Button
				testId="orders-drawer-confirm-yes"
				label={message(language, 'ordersDrawerOpen')}
				size="sm"
				state={pendingState('nosale')}
				pendingLabel={message(language, 'ordersDrawerOpening')}
				disabled={busy && pending !== 'nosale'}
				onPress={() => {
					askingDrawer = false;
					localFailure = '';
					onOpenDrawer();
				}}
			/>
			<Button
				testId="orders-drawer-confirm-no"
				label={message(language, 'ordersNo')}
				tone="text-quiet"
				size="sm"
				onPress={() => (askingDrawer = false)}
			/>
		{:else}
			<Button
				testId="orders-open-drawer"
				icon="drawer"
				label={message(language, 'ordersOpenDrawer')}
				tone="secondary"
				size="sm"
				state={pendingState('nosale')}
				pendingLabel={message(language, 'ordersDrawerOpening')}
				onPress={() => {
					localFailure = '';
					askingDrawer = true;
				}}
			/>
		{/if}
		<Button
			testId="orders-shift"
			icon="cash"
			label={message(language, 'ordersShift')}
			tone="secondary"
			size="sm"
			onPress={onShift}
		/>
		<Button
			testId="orders-test-print"
			icon="printer"
			label={message(language, 'ordersTestPrint')}
			tone="secondary"
			size="sm"
			state={pendingState('test')}
			pendingLabel={message(language, 'donePrinting')}
			disabled={busy && pending !== 'test'}
			onPress={() => {
				if (!hasReceiptPrinter) {
					localFailure = message(language, 'ordersNoReceiptPrinter');
				} else {
					localFailure = '';
					onTestPrint();
				}
			}}
		/>
		<Button
			testId="orders-printers"
			label={message(language, 'ordersPrinters')}
			tone="secondary"
			size="sm"
			onPress={onPrinters}
		/>
	</div>

	{#if dayDrivers.length > 0}
		<div class="drivers" data-testid="orders-drivers">
			<Segmented
				label={message(language, 'driversTitle')}
				options={driverFilters}
				value={driverShown}
				onChange={(value) => (byDriver = value)}
			/>
		</div>
	{/if}

	{#if update}
		<Notice life="standing" tone={update.tone} testId="orders-app-update">
			<span class="update">
				<span class="update-line">
					<span data-testid="orders-app-update-text"
						>{fill(message(language, update.text), leftToRight(update.version))}</span
					>
					{#if update.meter !== null}
						<Meter
							value={update.meter}
							tone="speed"
							label={message(language, 'appUpdateDownloading')}
							testId="orders-app-update-meter"
						/>
					{/if}
				</span>
				{#if update.action === 'install'}
					<Button
						testId="orders-app-update-install"
						label={message(language, 'appUpdateInstallNow')}
						size="sm"
						state={installWaiting ? 'pending' : 'idle'}
						pendingLabel={message(language, 'appUpdateInstallWaiting')}
						onPress={onInstallNow}
					/>
				{:else if update.action}
					<Button
						testId="orders-app-update-press"
						label={message(language, 'appUpdateAction')}
						size="sm"
						state={update.action === 'downloading' || appUpdate.slow ? 'pending' : 'idle'}
						pendingLabel={message(language, 'appUpdateDownloadingAction')}
						onPress={() => void appUpdate.press()}
					/>
				{/if}
			</span>
		</Notice>
	{/if}

	{#if effectiveFailure}
		<Band tone="flag" testId="orders-error">{effectiveFailure}</Band>
	{/if}

	{#if held.length > 0}
		<TablePanel
			title={message(language, 'printWaitingDecision')}
			columns={heldColumns}
			testId="orders-held"
		>
			{#each held as row (row.job.id)}
				{@const id = row.job.id}
				<Row testId="held-row-{id}" state="unconfirmed">
					<span class="strong" data-testid="held-doc-{id}"
						>{message(language, documents[row.job.document])}</span
					>
					{#if row.number}
						<span class="n strong" dir="ltr" data-testid="held-order-{id}">{row.number}</span>
					{:else if !row.job.orderId}
						<!-- A cash movement's kick, or a retry of one, names its
						     direction (UC-67); any other kick of no order is a no-sale open. -->
						<span class="soft" data-testid="held-order-{id}"
							>{message(
								language,
								row.job.cashDirection === 'out'
									? 'shiftCashOut'
									: row.job.cashDirection === 'in'
										? 'shiftCashIn'
										: 'printHeldNoSale',
							)}</span
						>
					{:else}
						<span></span>
					{/if}
					<span class="n soft" dir="ltr">{formatClock(row.job.createdAt, language)}</span>
					<span class="actions">
						<Button
							testId="held-decide-{id}"
							label={message(language, 'printDecide')}
							size="sm"
							disabled={busy}
							onPress={() => (deciding = id)}
						/>
					</span>
				</Row>
				{#if deciding === id}
					<div class="under" data-testid="held-choices">
						<span class="ask">{message(language, 'printHeldAsk')}</span>
						{#each row.choices as decision (decision)}
							{@const choice = choiceOf(row.job, decision)}
							<Button
								testId="held-choice-{decision}"
								label={message(language, choice.label)}
								tone="secondary"
								size="sm"
								state={pendingState(`decide:${id}:${decision}`)}
								pendingLabel={message(language, choice.pending)}
								disabled={busy && pending !== `decide:${id}:${decision}`}
								onPress={() => onDecide(row.job, decision)}
							/>
						{/each}
						<Button
							testId="held-choice-no"
							label={message(language, 'ordersNo')}
							tone="text-quiet"
							size="sm"
							disabled={busy}
							onPress={() => (deciding = '')}
						/>
					</div>
				{/if}
				{#if decideFailure?.jobId === id}
					<p class="refused" role="alert" data-testid="held-refused-{id}">
						{decideFailure.text}
					</p>
				{/if}
			{/each}
		</TablePanel>
	{/if}

	{#if loading}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'ordersLoadingDay')}
				caption={message(language, 'ordersLoadingDay')}
			/>
		</div>
	{:else if loadFailed && !day}
		<Band
			tone="flag"
			testId="orders-load-error"
			action={message(language, 'doneRetry')}
			actionTestId="orders-retry"
			onAction={onRetry}
		>
			{message(language, 'ordersLoadFailed')}
		</Band>
	{:else if day}
		<p class="summary" data-testid="orders-summary">
			{#each summary as item (item.key)}
				<span class="stat"
					>{message(language, item.key as MessageKey)}
					<bdi class="n" dir="ltr">{item.value}</bdi></span
				>
			{/each}
		</p>
		{#if orders.length === 0}
			<Card pad>
				<p class="empty" data-testid="orders-empty">
					{all.length > 0
						? message(language, 'ordersEmptyFilter')
						: message(language, 'ordersEmpty')}
				</p>
			</Card>
		{:else}
			<TablePanel {columns} testId="orders-list">
				{#each orders as order (order.id)}
					{@const status = statusOf(order)}
					<Row
						testId="order-row-{order.number}"
						state={order.status === 'open' ? 'unconfirmed' : 'idle'}
					>
						<span class="n strong" dir="ltr">{orderNumber(order)}</span>
						<span class="when">
							{#if order.tradingDay && day && order.tradingDay !== day.summary.tradingDay}
								<span class="day" data-testid="order-day-{order.number}"
									>{tradingDayLabel(order.tradingDay, language)}</span
								>
							{/if}
							<span class="n soft" dir="ltr">{formatClock(order.placedAt, language)}</span>
						</span>
						<span class="soft" data-testid="order-type-{order.number}">{typeOf(order)}</span>
						<span class="cell">
							<Chip
								text={message(language, status.key)}
								tone={status.tone}
								size="md"
								dot
								testId="order-status-{order.number}"
							/>
							{#if order.status === 'open' && order.billChangedSincePrinted}
								<Chip
									text={message(language, 'sellBillChanged')}
									tone="flag-solid"
									size="md"
									testId="ord-bill-changed-{order.number}"
								/>
							{/if}
						</span>
						<span class="n strong total" dir="ltr" data-testid="order-total-{order.number}">
							{formatMinor(order.totalMinor, minorDigits)}
						</span>
						<span class="actions">
							{#if order.status === 'open'}
								{#if !canOpen}
									<span class="why" data-testid="order-send-first-{order.number}"
										>{message(language, 'sellSendFirst')}</span
									>
								{/if}
								<Button
									testId="order-open-{order.number}"
									label={message(language, 'ordersOpenOnSell')}
									size="sm"
									disabled={busy || !canOpen}
									onPress={() => onOpen(order)}
								/>
							{:else}
								<Button
									testId="order-reprint-{order.number}"
									label={message(language, 'ordersReprint')}
									tone="text"
									size="sm"
									state={pendingState(`print:${order.id}`)}
									pendingLabel={message(language, 'donePrinting')}
									disabled={busy && pending !== `print:${order.id}`}
									onPress={() => onReprint(order)}
								/>
							{/if}
							<Button
								testId="order-details-{order.number}"
								label={detail === order.id
									? message(language, 'ordersCloseDetails')
									: message(language, 'ordersDetails')}
								trailingIcon={detail === order.id ? 'up' : 'expand'}
								expanded={detail === order.id}
								tone="secondary"
								size="sm"
								onPress={() => {
									// Void lives in the details, so its question closes with them.
									voiding = '';
									detail = detail === order.id ? '' : order.id;
								}}
							/>
						</span>
					</Row>
					{#if detail === order.id}
						<div class="under detail" data-testid="order-detail-{order.number}">
							<div class="lines">
								{#each order.lines ?? [] as line, index (index)}
									<div class="line">
										<span
											><bdi class="n" dir="ltr">{line.qty}×</bdi>
											{lineName(language, line.name, line.grams)}
											<span class="soft">{modifiersOf(line)}</span></span
										>
										<span class="n" dir="ltr">{formatMinor(line.lineTotalMinor, minorDigits)}</span>
									</div>
								{/each}
								{#if order.discount}
									<div class="line given" data-testid="order-discount-{order.number}">
										<span
											>{fill(
												message(language, 'ordersDiscount'),
												discountLabel(language, order.discount, minorDigits),
											)}</span
										>
										<span class="n" dir="ltr">{formatMinor(-order.discountMinor, minorDigits)}</span
										>
									</div>
								{/if}
							</div>
							<div class="facts">
								<span
									>{order.cashierName ?? ''} · {order.status === 'open' && order.type !== 'delivery'
										? message(language, 'ordersOpen')
										: payLine(language, order, minorDigits)}</span
								>
								{#if order.customerName || order.deliveryAddress}
									<span
										>{[order.customerName, order.deliveryAddress].filter(Boolean).join(' · ')}</span
									>
								{/if}
								{#if order.customerPhone}
									<span class="n phone" dir="ltr">{order.customerPhone}</span>
								{/if}
								{#if order.voidReason}
									<span>{message(language, 'ordersVoidReason')} {order.voidReason}</span>
								{/if}
								<span class="soft">{printed(order.printCount)}</span>
								{#if order.status === 'open' && order.type === 'delivery'}
									<span class="void">
										<Button
											testId="order-change-driver-{order.number}"
											label={message(language, 'driverChange')}
											tone="secondary"
											size="sm"
											disabled={busy}
											onPress={() => onChangeDriver(order)}
										/>
									</span>
								{/if}
								{#if order.status !== 'void'}
									<span class="void">
										<Button
											testId="order-void-{order.number}"
											label={message(language, 'ordersVoidAction')}
											tone="secondary"
											size="sm"
											disabled={busy}
											onPress={() => (voiding = order.id)}
										/>
									</span>
								{/if}
							</div>
						</div>
					{/if}
					{#if voiding === order.id}
						<div class="under" data-testid="void-sheet">
							<span class="ask">
								{message(language, 'ordersVoidWhy')}
								<bdi class="n" dir="ltr">{orderNumber(order)}</bdi>{message(
									language,
									'ordersVoidWhyEnd',
								)}
							</span>
							{#each voidReasons as reason (reason.key)}
								{@const words = message(language, reason.words)}
								<Button
									testId="void-reason-{reason.words}"
									label={words}
									tone="secondary"
									size="sm"
									state={pendingState(`void:${order.id}:${words}`)}
									pendingLabel={message(language, 'ordersVoiding')}
									disabled={busy && pending !== `void:${order.id}:${words}`}
									onPress={() => {
										voiding = '';
										onVoid(order, reason.key, words);
									}}
								/>
							{/each}
							<Button
								testId="void-cancel"
								label={message(language, 'ordersNo')}
								tone="text-quiet"
								size="sm"
								disabled={busy}
								onPress={() => (voiding = '')}
							/>
						</div>
					{/if}
					{#if voidClosedFor === order.id}
						<p class="refused" role="alert" data-testid="void-closed-{order.number}">
							{message(language, 'ordersVoidClosed')}
						</p>
					{/if}
					{#if sends?.waiting.includes(order.id) || sends?.refused[order.id]}
						<div class="under send" data-testid="order-waiting-{order.number}">
							<Chip text={message(language, 'ordersWaiting')} tone="unconfirmed" size="md" dot />
							<span class="tell">{message(language, 'ordersWaitingTell')}</span>
						</div>
					{/if}
				{/each}
			</TablePanel>
		{/if}
	{/if}
</div>

<style>
	.orders {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7);
		overflow-y: auto;
		overflow-x: hidden;
	}

	.bar {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
	}

	.grow {
		flex: 1;
	}

	/* The drivers' filter scrolls on its own when a day names many. */
	.drivers {
		flex: none;
		max-inline-size: 100%;
		overflow-x: auto;
	}

	.ask {
		font-size: var(--text-sm);
		font-weight: 600;
	}

	/* The update's line, with its meter under it while it downloads, and
	   Update app beside it, as the printers' Notice holds its ask. */
	.update {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	.update-line {
		flex: 1 1 280px;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.wait {
		flex: 1;
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.summary {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2) var(--space-7);
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.stat {
		white-space: nowrap;
	}

	.stat .n {
		font-weight: 700;
		color: var(--text-strong);
	}

	.n {
		font-family: var(--font-mono);
	}

	.strong {
		font-weight: 700;
	}

	.soft {
		color: var(--text-body);
	}

	.cell {
		min-inline-size: 0;
		display: flex;
	}

	/* Numbers in the table read left to right and keep their column's start,
	   as the design sets them. */
	.n.strong,
	.n.soft {
		min-inline-size: 0;
		text-align: start;
		white-space: nowrap;
	}

	.total {
		text-align: end;
	}

	.actions {
		min-inline-size: 0;
		display: flex;
		gap: var(--space-3);
		justify-content: flex-end;
		flex-wrap: wrap;
	}

	/* An open order of an earlier trading day says its date above its time. */
	.when {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		white-space: nowrap;
	}

	.day {
		font-size: var(--text-micro);
		font-weight: 600;
		color: var(--text-strong);
	}

	.why {
		align-self: center;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.under {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3);
		padding: var(--row-pad);
		background: var(--surface-card-header);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.refused {
		margin: 0;
		padding: var(--row-pad);
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--ink-flag-deep);
		background: var(--surface-flag);
		border-block-end: var(--border-width) solid var(--border-flag);
	}

	/* A sale that has not reached Bosla: its chip, then why, under its row. */
	.send {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-4);
	}

	.send .tell {
		flex: 1 1 280px;
		min-inline-size: 0;
		font-size: var(--text-sm);
	}

	.under.send .tell {
		color: var(--text-body);
	}

	.detail {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
		align-items: start;
		gap: var(--space-7);
		font-size: var(--text-sm);
	}

	.lines,
	.facts {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.facts {
		color: var(--text-body);
	}

	.line {
		display: flex;
		gap: var(--space-4);
		justify-content: space-between;
	}

	.void {
		display: flex;
		padding-block-start: var(--space-2);
	}

	.phone {
		text-align: start;
	}

	.given {
		padding-block-start: var(--space-2);
		border-block-start: var(--border-width) solid var(--border-inset);
	}
</style>
